import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import EntityPicker from '../components/EntityPicker';

/**
 * Review Queue — conversation inbox.
 *
 * Items are grouped by the conversation they came from (GET /api/review/queue/grouped)
 * so a reviewer works through conversations, not a flat list of database rows. All the
 * plain-language wording (what needs attention / why it's flagged / what the AI said)
 * is produced server-side in routes/review.py::_humanize_item, so this file stays a
 * presentation layer.
 *
 * Decisions still POST to /api/review/<id> one item at a time — that writeback logic
 * is unchanged.
 */

// Fixed label vocabularies (mirrors VALID_LABELS in routes/review.py). Not org config.
const LABEL_OPTIONS = {
  sentiment_label: ['positive', 'negative', 'neutral'],
  outcome_classification: ['CONVERTED', 'NOT_CONVERTED', 'FOLLOW_UP', 'LOST', 'UNKNOWN'],
};

const STATUSES = ['PENDING', 'APPROVED', 'REJECTED', 'CORRECTED'];

const s = {
  page: { padding: '24px', fontFamily: 'inherit', maxWidth: '1000px', margin: '0 auto' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px', flexWrap: 'wrap', gap: '12px' },
  title: { fontSize: '22px', fontWeight: 700, color: '#1a1a1a', margin: 0 },
  subtitle: { fontSize: '13px', color: '#6c757d', marginBottom: '18px' },
  controls: { display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '16px' },
  select: {
    padding: '6px 10px', border: '1px solid #ced4da', borderRadius: '6px',
    fontSize: '13px', background: '#fff', cursor: 'pointer',
  },
  refreshBtn: {
    padding: '6px 16px', background: '#007bff', color: '#fff', border: 'none',
    borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 600,
  },
  linkBtn: {
    fontSize: '13px', color: '#007bff', background: 'none', border: 'none',
    padding: 0, cursor: 'pointer', textDecoration: 'underline',
  },
  summary: {
    display: 'flex', gap: '20px', padding: '12px 16px', background: '#f8f9fa',
    border: '1px solid #e9ecef', borderRadius: '8px', marginBottom: '18px', flexWrap: 'wrap',
  },
  summaryNum: { fontSize: '20px', fontWeight: 700, color: '#1a1a1a' },
  summaryLabel: { fontSize: '11px', color: '#6c757d', fontWeight: 600, textTransform: 'uppercase' },

  card: {
    border: '1px solid #e9ecef', borderRadius: '10px', marginBottom: '12px',
    background: '#fff', overflow: 'hidden',
  },
  cardHead: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '14px 18px', cursor: 'pointer', gap: '12px',
  },
  convName: { fontSize: '15px', fontWeight: 700, color: '#1a1a1a' },
  convMeta: { fontSize: '12px', color: '#6c757d', marginTop: '3px' },
  expandCue: {
    fontSize: '12px', fontWeight: 600, color: '#007bff', whiteSpace: 'nowrap',
    border: '1px solid #cfe2ff', borderRadius: '6px', padding: '4px 10px', background: '#f1f6ff',
  },
  steps: {
    display: 'flex', gap: '14px', flexWrap: 'wrap', padding: '12px 16px',
    background: '#f1f6ff', border: '1px solid #cfe2ff', borderRadius: '8px',
    marginBottom: '18px', fontSize: '13px', color: '#1a3d6b',
  },
  stepNum: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: '18px', height: '18px', borderRadius: '50%', background: '#007bff',
    color: '#fff', fontSize: '11px', fontWeight: 700, marginRight: '6px', flexShrink: 0,
  },
  sectionLabel: {
    fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', color: '#6c757d',
    letterSpacing: '0.03em', marginTop: '14px', marginBottom: '5px',
  },
  helpText: { fontSize: '12px', color: '#6c757d', marginTop: '8px', lineHeight: 1.5 },
  pill: (n) => ({
    display: 'inline-block', padding: '3px 10px', borderRadius: '12px', fontSize: '12px',
    fontWeight: 700, color: '#fff', background: n > 0 ? '#fd7e14' : '#adb5bd', whiteSpace: 'nowrap',
  }),
  chevron: { fontSize: '13px', color: '#6c757d', width: '14px', textAlign: 'center' },

  item: { borderTop: '1px solid #f0f0f0', padding: '14px 18px', background: '#fbfcfd' },
  itemHead: { display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap' },
  typeTag: {
    fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', color: '#495057',
    background: '#e9ecef', padding: '2px 8px', borderRadius: '10px',
  },
  itemWhat: { fontSize: '14px', fontWeight: 600, color: '#1a1a1a' },
  itemIssue: { fontSize: '13px', color: '#6c4400', marginTop: '6px' },
  quote: {
    borderLeft: '3px solid #007bff', padding: '8px 12px', background: '#f1f6ff',
    color: '#1a1a1a', fontStyle: 'italic', margin: '10px 0', whiteSpace: 'pre-wrap',
    wordBreak: 'break-word', fontSize: '13px',
  },
  kv: { display: 'flex', gap: '8px', fontSize: '13px', margin: '6px 0', flexWrap: 'wrap' },
  kvLabel: { color: '#6c757d', fontWeight: 600 },
  formRow: { display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end', marginTop: '12px' },
  input: {
    padding: '6px 10px', border: '1px solid #ced4da', borderRadius: '6px',
    fontSize: '13px', minWidth: '160px',
  },
  fieldLabel: { fontSize: '11px', color: '#6c757d', marginBottom: '3px' },
  approveBtn: (enabled) => ({
    padding: '6px 18px', background: enabled ? '#28a745' : '#e9ecef',
    color: enabled ? '#fff' : '#adb5bd', border: 'none', borderRadius: '6px',
    cursor: enabled ? 'pointer' : 'not-allowed', fontSize: '13px', fontWeight: 600,
  }),
  rejectBtn: (enabled) => ({
    padding: '6px 18px', background: enabled ? '#dc3545' : '#e9ecef',
    color: enabled ? '#fff' : '#adb5bd', border: 'none', borderRadius: '6px',
    cursor: enabled ? 'pointer' : 'not-allowed', fontSize: '13px', fontWeight: 600,
  }),
  saveBtn: (enabled) => ({
    padding: '6px 18px', background: enabled ? '#007bff' : '#e9ecef',
    color: enabled ? '#fff' : '#adb5bd', border: 'none', borderRadius: '6px',
    cursor: enabled ? 'pointer' : 'not-allowed', fontSize: '13px', fontWeight: 600,
  }),
  pagination: { display: 'flex', alignItems: 'center', gap: '12px', marginTop: '18px', fontSize: '13px' },
  pageBtn: {
    padding: '5px 14px', border: '1px solid #ced4da', borderRadius: '6px',
    background: '#fff', cursor: 'pointer', fontSize: '13px',
  },
  errorBanner: {
    padding: '10px 14px', background: '#fff3cd', borderRadius: '6px',
    color: '#856404', marginBottom: '12px', fontSize: '13px',
  },
  empty: {
    padding: '40px 20px', textAlign: 'center', color: '#6c757d', fontSize: '14px',
    border: '1px dashed #dee2e6', borderRadius: '10px',
  },
  modalWrap: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex',
    alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '24px',
  },
  modalBox: {
    background: '#fff', borderRadius: '8px', width: '100%', maxWidth: '900px',
    maxHeight: '90vh', display: 'flex', flexDirection: 'column',
    boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
  },
  modalHead: {
    padding: '14px 20px', borderBottom: '1px solid #e9ecef', display: 'flex',
    alignItems: 'center', justifyContent: 'space-between', gap: '12px',
  },
  modalClose: {
    background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer',
    color: '#6c757d', lineHeight: 1, padding: '0 4px',
  },
};

function AdminTasks({ currentUser }) {
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState('');
  // Cross-org switching is driven by the org's `type` in the central org config
  // (an admin-type org can review other orgs' queues) — never by a hard-coded
  // organization name. Everyone else is locked to their own org.
  const [canSwitchOrg, setCanSwitchOrg] = useState(false);

  const [conversations, setConversations] = useState([]);
  const [reviewed, setReviewed] = useState([]);   // already-reviewed history (non-PENDING filter)
  const [totalConversations, setTotalConversations] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [filterStatus, setFilterStatus] = useState('PENDING');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [openConvId, setOpenConvId] = useState(null);
  const [openItemId, setOpenItemId] = useState(null);
  const [form, setForm] = useState({});
  const [formOriginal, setFormOriginal] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const [entityTypeOptions, setEntityTypeOptions] = useState([]);
  const [docModal, setDocModal] = useState(null);
  const [transcriptModal, setTranscriptModal] = useState(null);

  // Organizations available to this user. Whether the org picker appears is decided
  // by the user's own org type in the central config, not by any org name.
  useEffect(() => {
    const userOrg = (currentUser?.organization || '').trim().toLowerCase();
    axios.get('/api/config/organizations')
      .then(res => {
        const all = res.data.data || [];
        const ownEntry = all.find(o => o.name === userOrg);
        const isAdminOrg = ownEntry?.type === 'admin';
        // Admin orgs are operators, not data orgs — never offer them as a target.
        const selectable = all.filter(o => o.type !== 'admin');
        setCanSwitchOrg(isAdminOrg && selectable.length > 0);
        setOrganizations(selectable);
        if (isAdminOrg) {
          const match = selectable.find(o => o.name === userOrg);
          setSelectedOrg(match ? match.name : (selectable[0]?.name || ''));
        } else {
          setSelectedOrg(userOrg);
        }
      })
      .catch(() => {
        // No org list available (e.g. restricted user) — fall back to the session org.
        setOrganizations([]);
        setCanSwitchOrg(false);
        setSelectedOrg(userOrg);
      });
  }, [currentUser]);

  const orgParam = selectedOrg ? { organization: selectedOrg } : {};

  useEffect(() => {
    if (!selectedOrg) { setEntityTypeOptions([]); return; }
    axios.get('/api/config/available-entity-types', { params: { organization: selectedOrg } })
      .then(res => setEntityTypeOptions(res.data?.entity_types || []))
      .catch(() => setEntityTypeOptions([]));
  }, [selectedOrg]);

  const fetchInbox = useCallback(() => {
    if (!selectedOrg) return;
    setLoading(true);
    setError(null);
    axios.get('/api/review/queue/grouped', {
      params: { ...orgParam, status: filterStatus, page, page_size: 20 },
    })
      .then(res => {
        setConversations(res.data.conversations || []);
        setReviewed(res.data.reviewed || []);
        setTotalConversations(res.data.total_conversations || 0);
        setTotalItems(res.data.total_items || 0);
        setPages(res.data.pages || 1);
      })
      .catch(err => setError(err.response?.data?.error || err.message))
      .finally(() => setLoading(false));
  }, [selectedOrg, filterStatus, page]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchInbox(); }, [fetchInbox]);

  const refresh = () => {
    setOpenItemId(null);
    fetchInbox();
  };

  // Opening an item pre-fills the correction form from the values the API sent,
  // and snapshots them so Save stays disabled until something actually changes.
  const openItem = (item) => {
    if (openItemId === item.review_id) {
      setOpenItemId(null);
      return;
    }
    setOpenItemId(item.review_id);
    setSubmitError(null);
    let fresh;
    if (item.form_mode === 'entity') {
      fresh = { corrected_entity_name: item.entity_name || '', corrected_entity_type: item.entity_type || '' };
    } else if (item.form_mode === 'discovered') {
      fresh = {
        corrected_entity_name: item.entity_name || '',
        corrected_entity_type: item.entity_type || '',
        merge_target_entity_id: '',
      };
    } else if (item.label_options) {
      // Cards with their own answer list start on their suggested answer (may be none).
      fresh = { corrected_label: item.prefill_label || '' };
    } else {
      fresh = { corrected_label: item.current_label || '' };
    }
    setForm({ ...fresh, reviewer_notes: '' });
    setFormOriginal(fresh);
  };

  const hasChanges = () =>
    Object.keys(formOriginal).some(k => (form[k] || '') !== (formOriginal[k] || ''));

  const submit = (item, status) => {
    setSubmitError(null);
    const reviewedBy = (currentUser?.username || '').trim();
    if (!reviewedBy) {
      setSubmitError('Cannot submit: no logged-in user.');
      return;
    }

    let payload;
    if (item.form_mode === 'discovered') {
      const name = (form.corrected_entity_name || '').trim();
      const type = (form.corrected_entity_type || '').trim();
      const mergeId = form.merge_target_entity_id;
      if (status === 'CORRECTED' && !mergeId && (!name || !type)) {
        setSubmitError('Name and category are required — or pick an entity to merge into.');
        return;
      }
      payload = { corrected_entity_name: name, corrected_entity_type: type };
      if (mergeId) payload.merge_target_entity_id = Number(mergeId);
    } else if (item.form_mode === 'entity') {
      const name = (form.corrected_entity_name || '').trim();
      const type = (form.corrected_entity_type || '').trim();
      if (status === 'CORRECTED' && (!name || !type)) {
        setSubmitError('Name and category are both required to save a correction.');
        return;
      }
      payload = { corrected_entity_name: name, corrected_entity_type: type };
    } else {
      const label = (form.corrected_label || '').trim();
      if (status === 'CORRECTED' && !label) {
        setSubmitError('A corrected value is required to save.');
        return;
      }
      payload = { corrected_label: label };
    }
    payload.review_status = status;
    payload.reviewed_by = reviewedBy;
    if ((form.reviewer_notes || '').trim()) payload.reviewer_notes = form.reviewer_notes.trim();

    setSubmitting(true);
    axios.post(
      `/api/review/${item.review_id}${selectedOrg ? `?organization=${encodeURIComponent(selectedOrg)}` : ''}`,
      payload,
    )
      .then(() => {
        setOpenItemId(null);
        fetchInbox();
      })
      .catch(err => setSubmitError(err.response?.data?.error || err.message))
      .finally(() => setSubmitting(false));
  };

  const openTranscript = (convId, filename) => {
    if (!convId) return;
    const params = { ...orgParam };
    if (filename) params.filename = filename;
    setTranscriptModal({ title: filename || `Conversation ${convId}`, loading: true });
    axios.get(`/api/field-hq/conversation/${convId}/transcript`, { params })
      .then(res => setTranscriptModal({
        title: filename || `Conversation ${convId}`,
        loading: false,
        text: res.data?.transcript || '',
        source: res.data?.source,
      }))
      .catch(err => setTranscriptModal({
        title: filename || `Conversation ${convId}`,
        loading: false,
        error: err.response?.data?.error || err.message,
      }));
  };

  const openDocs = () => {
    axios.get('/api/docs/review-queue')
      .then(res => setDocModal(res.data?.success
        ? { title: res.data.title, html: res.data.html }
        : { title: 'Documentation', html: `<p>${res.data?.error || 'Could not load doc.'}</p>` }))
      .catch(err => setDocModal({ title: 'Documentation', html: `<p>Could not load doc: ${err.message}</p>` }));
  };

  const formatDate = (d) => {
    if (!d) return null;
    try { return new Date(d).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }); }
    catch { return d; }
  };

  // ── Correction form for one item ─────────────────────────────────────────
  const renderForm = (item) => {
    const changed = hasChanges();
    // Keep accepts the suggested answer, so it needs one when the card lists its own answers.
    const approveEnabled = !submitting && !changed && !(item.label_options && !form.corrected_label);
    const rejectEnabled = !submitting;
    const saveEnabled = !submitting && changed;
    // [{value, label}] — the card's own answers, else the fixed list for its type.
    const labelOptions =
      item.label_options || LABEL_OPTIONS[item.item_type]?.map(o => ({ value: o, label: o }));

    return (
      <>
        <div style={s.formRow}>
          {item.form_mode === 'discovered' ? (
            <>
              <div>
                <div style={s.fieldLabel}>Name — fix the spelling if it’s wrong</div>
                <input
                  type="text"
                  style={s.input}
                  value={form.corrected_entity_name || ''}
                  onChange={e => setForm(f => ({ ...f, corrected_entity_name: e.target.value }))}
                />
              </div>
              <div>
                <div style={s.fieldLabel}>Category — what kind of thing is it?</div>
                <select
                  style={s.select}
                  value={form.corrected_entity_type || ''}
                  onChange={e => setForm(f => ({ ...f, corrected_entity_type: e.target.value }))}
                >
                  <option value="">— select —</option>
                  {(() => {
                    const seen = new Set();
                    const opts = [];
                    const cur = form.corrected_entity_type;
                    if (cur) { seen.add(cur); opts.push(cur); }
                    entityTypeOptions.forEach(t => { if (!seen.has(t)) { seen.add(t); opts.push(t); } });
                    return opts.map(t => <option key={t} value={t}>{t}</option>);
                  })()}
                </select>
              </div>
              <div>
                <div style={s.fieldLabel}>Or merge into an existing entity</div>
                <EntityPicker
                  organization={selectedOrg}
                  placeholder={form.merge_target_entity_id ? '✓ will merge into selected' : 'Search all entities…'}
                  onSelect={en => setForm(f => ({ ...f, merge_target_entity_id: en.entity_id }))}
                />
              </div>
            </>
          ) : item.form_mode === 'entity' ? (
            <>
              <div>
                <div style={s.fieldLabel}>Name — fix the spelling if it’s wrong</div>
                <input
                  type="text"
                  style={s.input}
                  value={form.corrected_entity_name || ''}
                  onChange={e => setForm(f => ({ ...f, corrected_entity_name: e.target.value }))}
                />
              </div>
              <div>
                <div style={s.fieldLabel}>Category — what kind of thing is it?</div>
                <select
                  style={s.select}
                  value={form.corrected_entity_type || ''}
                  onChange={e => setForm(f => ({ ...f, corrected_entity_type: e.target.value }))}
                >
                  <option value="">— select —</option>
                  {(() => {
                    const seen = new Set();
                    const opts = [];
                    const cur = form.corrected_entity_type;
                    if (cur) { seen.add(cur); opts.push(cur); }
                    entityTypeOptions.forEach(t => { if (!seen.has(t)) { seen.add(t); opts.push(t); } });
                    return opts.map(t => <option key={t} value={t}>{t}</option>);
                  })()}
                </select>
              </div>
              <div>
                {/* Merge into an existing entity by searching name/alias across all
                    types. Picking one fills Name+Category with its canonical values;
                    the existing name-based merge in _submit_mention_review does the rest. */}
                <div style={s.fieldLabel}>Or merge into an existing entity</div>
                <EntityPicker
                  organization={selectedOrg}
                  placeholder="Search all entities…"
                  onSelect={en => setForm(f => ({
                    ...f,
                    corrected_entity_name: en.entity_name,
                    corrected_entity_type: en.entity_type,
                  }))}
                />
              </div>
            </>
          ) : item.form_mode === 'entity_type' ? (
            <div>
              <div style={s.fieldLabel}>Entity type</div>
              <select
                style={s.select}
                value={form.corrected_label || ''}
                onChange={e => setForm(f => ({ ...f, corrected_label: e.target.value }))}
              >
                <option value="">— select —</option>
                {(() => {
                  // Available entity types from dim_entity, with the current value first.
                  const seen = new Set();
                  const opts = [];
                  const cur = form.corrected_label;
                  if (cur) { seen.add(cur); opts.push(cur); }
                  entityTypeOptions.forEach(t => { if (!seen.has(t)) { seen.add(t); opts.push(t); } });
                  return opts.map(t => <option key={t} value={t}>{t}</option>);
                })()}
              </select>
            </div>
          ) : (
            <div>
              <div style={s.fieldLabel}>The right answer — change it if the AI got it wrong</div>
              {labelOptions ? (
                <select
                  style={s.select}
                  value={form.corrected_label || ''}
                  onChange={e => setForm(f => ({ ...f, corrected_label: e.target.value }))}
                >
                  <option value="">— select —</option>
                  {labelOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              ) : (
                <input
                  type="text"
                  style={s.input}
                  value={form.corrected_label || ''}
                  onChange={e => setForm(f => ({ ...f, corrected_label: e.target.value }))}
                />
              )}
            </div>
          )}
          <div style={{ flex: 1, minWidth: '180px' }}>
            <div style={s.fieldLabel}>Note (optional)</div>
            <input
              type="text"
              style={{ ...s.input, width: '100%' }}
              placeholder="Anything worth recording"
              value={form.reviewer_notes || ''}
              onChange={e => setForm(f => ({ ...f, reviewer_notes: e.target.value }))}
            />
          </div>
        </div>

        <div style={{ ...s.formRow, marginTop: '14px' }}>
          <button
            style={s.approveBtn(approveEnabled)}
            onClick={() => submit(item, 'APPROVED')}
            disabled={!approveEnabled}
          >
            Keep
          </button>
          <button
            style={s.rejectBtn(rejectEnabled)}
            onClick={() => submit(item, 'REJECTED')}
            disabled={!rejectEnabled}
          >
            Remove
          </button>
          <button
            style={s.saveBtn(saveEnabled)}
            onClick={() => submit(item, 'CORRECTED')}
            disabled={!saveEnabled}
          >
            {submitting ? 'Saving…' : 'Save'}
          </button>
        </div>

        {submitError && (
          <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '8px' }}>{submitError}</div>
        )}
      </>
    );
  };

  // ── One flagged item inside a conversation ───────────────────────────────
  const renderItem = (conv, item) => {
    const isOpen = openItemId === item.review_id;
    return (
      <div key={item.review_id} style={s.item}>
        <div
          style={{ ...s.itemHead, cursor: 'pointer', justifyContent: 'space-between' }}
          onClick={() => openItem(item)}
        >
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap' }}>
            <span style={s.typeTag}>{item.type_label}</span>
            <span style={s.itemWhat}>{item.what}</span>
            {item.mention_count > 1 && (
              <span style={{ fontSize: '12px', color: '#6c757d' }}>
                said {item.mention_count} times in this call
              </span>
            )}
          </div>
          <span style={s.expandCue}>{isOpen ? 'Close ▴' : 'Check this ▾'}</span>
        </div>
        <div style={s.itemIssue}>{item.issue}</div>

        {isOpen && (
          <div style={{ marginTop: '4px' }}>
            {item.segment && (
              <>
                <div style={s.sectionLabel}>What was actually said</div>
                <div style={s.quote}>“{item.segment}”</div>
              </>
            )}

            <div style={s.sectionLabel}>What the AI wrote down</div>
            <div style={{ fontSize: '13px', color: '#1a1a1a' }}>{item.ai_output || '—'}</div>
            {item.expected && (
              <>
                <div style={s.sectionLabel}>Already corrected to</div>
                <div style={{ fontSize: '13px', color: '#1a1a1a' }}>{item.expected}</div>
              </>
            )}

            {conv.conversation_id && (
              <div style={{ marginTop: '10px' }}>
                <button
                  type="button"
                  style={{ ...s.linkBtn, fontSize: '12px' }}
                  onClick={() => openTranscript(conv.conversation_id, item.transcript_file)}
                >
                  Read the full call transcript
                </button>
              </div>
            )}

            <div style={s.sectionLabel}>Your decision</div>
            {item.mention_count > 1 && (
              <div style={{ ...s.helpText, marginTop: 0, marginBottom: '4px' }}>
                Heads up: this was said {item.mention_count} times in this call — whatever you
                choose applies to all {item.mention_count}.
              </div>
            )}
            {renderForm(item)}
          </div>
        )}
      </div>
    );
  };

  // ── One conversation card ────────────────────────────────────────────────
  const renderConversation = (conv) => {
    const key = conv.conversation_id ?? 'unlinked';
    const isOpen = openConvId === key;
    const metaBits = [];
    if (conv.customer) metaBits.push(conv.customer);
    if (conv.region) metaBits.push(conv.region);
    if (conv.date) metaBits.push(formatDate(conv.date));

    return (
      <div key={key} style={s.card}>
        <div
          style={s.cardHead}
          onClick={() => { setOpenConvId(isOpen ? null : key); setOpenItemId(null); }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
            <span style={s.chevron}>{isOpen ? '▾' : '▸'}</span>
            <div style={{ minWidth: 0 }}>
              <div style={s.convName}>{conv.conversation_name}</div>
              {metaBits.length > 0 && <div style={s.convMeta}>{metaBits.join(' · ')}</div>}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={s.pill(conv.item_count)}>
              {conv.item_count} {conv.item_count === 1 ? 'thing' : 'things'} to check
            </span>
            <span style={s.expandCue}>
              {isOpen ? 'Hide ▴' : 'Open ▾'}
            </span>
          </div>
        </div>
        {isOpen && (
          <div style={{ ...s.helpText, padding: '0 18px 10px 42px', marginTop: 0 }}>
            Click any item below to see the exact words from the call and decide what to do.
          </div>
        )}
        {isOpen && conv.review_items.map(item => renderItem(conv, item))}
      </div>
    );
  };

  return (
    <div style={s.page}>
      <div style={s.header}>
        <h2 style={s.title}>Review Queue</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {canSwitchOrg ? (
            <>
              <label style={{ fontSize: '13px', fontWeight: 600, color: '#495057' }}>Organization:</label>
              <select
                style={{ ...s.select, minWidth: '150px' }}
                value={selectedOrg}
                onChange={e => { setSelectedOrg(e.target.value); setPage(1); setOpenConvId(null); setOpenItemId(null); }}
              >
                {organizations.map(o => (
                  <option key={o.name} value={o.name}>{o.display_name || o.name}</option>
                ))}
              </select>
            </>
          ) : null}
          <button style={s.refreshBtn} onClick={refresh}>Refresh</button>
        </div>
      </div>
      <div style={s.subtitle}>
        These are conversations where the AI wasn’t sure it got something right, and is asking
        you to confirm. Nothing here is broken — your answers also teach it to do better next time.
      </div>

      <div style={s.steps}>
        <div><span style={s.stepNum}>1</span>Click a conversation to see what it’s unsure about</div>
        <div><span style={s.stepNum}>2</span>Click an item to read the quote it came from</div>
        <div><span style={s.stepNum}>3</span>Confirm it, reject it, or type the correction</div>
      </div>

      <div style={s.summary}>
        <div>
          <div style={s.summaryNum}>{totalConversations}</div>
          <div style={s.summaryLabel}>Conversations waiting</div>
        </div>
        <div>
          <div style={s.summaryNum}>{totalItems}</div>
          <div style={s.summaryLabel}>Things to check</div>
        </div>
      </div>

      <div style={s.controls}>
        <span style={{ fontSize: '13px', color: '#6c757d' }}>Show:</span>
        <select
          style={s.select}
          value={filterStatus}
          onChange={e => { setFilterStatus(e.target.value); setPage(1); setOpenConvId(null); setOpenItemId(null); }}
        >
          {STATUSES.map(st => (
            <option key={st} value={st}>
              {st === 'PENDING' ? 'Still needs checking'
                : st === 'APPROVED' ? 'Already confirmed'
                : st === 'REJECTED' ? 'Already rejected'
                : 'Already corrected'}
            </option>
          ))}
        </select>
        <button type="button" onClick={openDocs} style={s.linkBtn} title="Read how the Review Queue works">
          How it works
        </button>
      </div>

      {error && <div style={s.errorBanner}>Error: {error}</div>}

      {loading && <div style={{ color: '#6c757d', fontSize: '14px', padding: '20px 0' }}>Loading…</div>}

      {/* PENDING view: the conversation inbox. */}
      {!loading && filterStatus === 'PENDING' && conversations.length === 0 && (
        <div style={s.empty}>
          <div style={{ fontSize: '15px', fontWeight: 600, color: '#495057', marginBottom: '6px' }}>
            All clear — nothing to check right now
          </div>
          The AI was confident about everything it processed. New items appear here
          automatically after the next batch of calls is analysed.
        </div>
      )}
      {!loading && filterStatus === 'PENDING' && conversations.map(renderConversation)}

      {/* Non-PENDING view: a flat, read-only history of what was already reviewed. */}
      {!loading && filterStatus !== 'PENDING' && reviewed.length === 0 && (
        <div style={s.empty}>Nothing here yet.</div>
      )}
      {!loading && filterStatus !== 'PENDING' && reviewed.map(r => (
        <div key={r.audit_id} style={{ ...s.card, padding: '12px 16px' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap' }}>
            <span style={{ ...s.typeTag }}>{r.type_label}</span>
            <span style={s.itemWhat}>{r.what}</span>
            <span style={{
              fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '10px', color: '#fff',
              background: r.decision === 'REJECTED' ? '#dc3545'
                : r.decision === 'APPROVED' ? '#28a745' : '#007bff',
            }}>{r.decision}</span>
          </div>
          {r.new_label && r.new_label !== r.what && (
            <div style={{ fontSize: '13px', color: '#343a40', marginTop: '4px' }}>
              → {r.new_label}
            </div>
          )}
          <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '4px' }}>
            {r.reviewed_by ? `by ${r.reviewed_by}` : ''}{r.review_date ? ` · ${formatDate(r.review_date)}` : ''}
            {r.reviewer_notes ? ` · ${r.reviewer_notes}` : ''}
          </div>
        </div>
      ))}

      {pages > 1 && (
        <div style={s.pagination}>
          <button style={s.pageBtn} onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}>
            ← Prev
          </button>
          <span style={{ color: '#6c757d' }}>Page {page} / {pages}</span>
          <button style={s.pageBtn} onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page >= pages}>
            Next →
          </button>
        </div>
      )}

      {/* In-app docs modal (rendered server-side from docs/REVIEW_QUEUE.md). */}
      {docModal && (
        <div onClick={() => setDocModal(null)} style={s.modalWrap}>
          <div onClick={e => e.stopPropagation()} style={s.modalBox}>
            <div style={s.modalHead}>
              <div style={{ fontSize: '16px', fontWeight: 700, color: '#1a1a1a' }}>{docModal.title}</div>
              <button onClick={() => setDocModal(null)} style={s.modalClose} aria-label="Close">×</button>
            </div>
            <style>{`
              .doc-modal-body { padding: 20px 24px; overflow-y: auto; font-size: 14px; line-height: 1.55; color: #1a1a1a; }
              .doc-modal-body h1, .doc-modal-body h2, .doc-modal-body h3 { margin-top: 1.2em; margin-bottom: 0.5em; color: #1a1a1a; }
              .doc-modal-body h1 { font-size: 22px; }
              .doc-modal-body h2 { font-size: 18px; border-bottom: 1px solid #e9ecef; padding-bottom: 6px; }
              .doc-modal-body h3 { font-size: 15px; }
              .doc-modal-body p { margin: 0.7em 0; }
              .doc-modal-body ul, .doc-modal-body ol { margin: 0.5em 0; padding-left: 1.6em; }
              .doc-modal-body li { margin: 0.2em 0; }
              .doc-modal-body a { color: #007bff; text-decoration: none; }
              .doc-modal-body a:hover { text-decoration: underline; }
              .doc-modal-body code { background: #f1f3f5; padding: 1px 5px; border-radius: 3px; font-size: 12.5px; font-family: 'SF Mono', Consolas, monospace; }
              .doc-modal-body pre { background: #f8f9fa; padding: 12px 14px; border-radius: 6px; overflow-x: auto; font-size: 12.5px; }
              .doc-modal-body pre code { background: none; padding: 0; }
              .doc-modal-body table { border-collapse: collapse; width: 100%; margin: 0.8em 0; font-size: 13px; }
              .doc-modal-body th, .doc-modal-body td { border: 1px solid #e9ecef; padding: 6px 10px; text-align: left; vertical-align: top; }
              .doc-modal-body th { background: #f8f9fa; font-weight: 600; }
              .doc-modal-body blockquote { border-left: 3px solid #007bff; padding: 4px 14px; background: #f1f6ff; color: #1a1a1a; margin: 0.8em 0; }
              .doc-modal-body hr { border: none; border-top: 1px solid #e9ecef; margin: 1.4em 0; }
            `}</style>
            <div className="doc-modal-body" dangerouslySetInnerHTML={{ __html: docModal.html }} />
          </div>
        </div>
      )}

      {/* Transcript viewer modal. */}
      {transcriptModal && (
        <div onClick={() => setTranscriptModal(null)} style={s.modalWrap}>
          <div onClick={e => e.stopPropagation()} style={s.modalBox}>
            <div style={s.modalHead}>
              <div
                style={{ fontSize: '14px', fontWeight: 700, color: '#1a1a1a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                title={transcriptModal.title}
              >
                {transcriptModal.title}
                {transcriptModal.source && (
                  <span style={{ marginLeft: '8px', fontSize: '11px', fontWeight: 500, color: '#6c757d' }}>
                    (source: {transcriptModal.source})
                  </span>
                )}
              </div>
              <button onClick={() => setTranscriptModal(null)} style={s.modalClose} aria-label="Close">×</button>
            </div>
            <div style={{
              padding: '20px 24px', overflowY: 'auto', fontSize: '13.5px', lineHeight: 1.55,
              color: '#1a1a1a', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            }}>
              {transcriptModal.loading && <div style={{ color: '#6c757d' }}>Loading transcript…</div>}
              {transcriptModal.error && (
                <div style={{ color: '#dc3545' }}>Could not load transcript: {transcriptModal.error}</div>
              )}
              {!transcriptModal.loading && !transcriptModal.error && (
                transcriptModal.text || <span style={{ color: '#6c757d' }}>Transcript is empty.</span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminTasks;
